import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import BrandLogo from './BrandLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Handles the return from Emergent Google Auth.
 * The URL will look like: https://our-app/dashboard#session_id=xxx
 * We parse the fragment, call backend to exchange for a session cookie, then navigate.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      navigate('/', { replace: true });
      return;
    }
    const sessionId = decodeURIComponent(match[1]);

    (async () => {
      try {
        const resp = await fetch(`${API}/auth/session`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sessionId }),
        });
        if (!resp.ok) throw new Error('Auth failed');
        const data = await resp.json();
        // Cache user locally so ProtectedRoute doesn't wait
        localStorage.setItem('atw_user', JSON.stringify(data.user));
        // Link this Google account to their creator (@handle) profile so /@you works across
        // devices. Non-blocking — failure here shouldn't break sign-in.
        try {
          const browserKey = localStorage.getItem('atw_author_key') || null;
          const linkResp = await fetch(`${API}/authors/link`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ author_key: browserKey }),
          });
          if (linkResp.ok) {
            const linkData = await linkResp.json();
            if (linkData.author_key) {
              localStorage.setItem('atw_author_key', linkData.author_key);
            }
          }
        } catch { /* noop */ }
        // Strip the hash then navigate
        window.history.replaceState({}, document.title, window.location.pathname);
        navigate('/build', { replace: true, state: { user: data.user } });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Auth callback failed', e);
        navigate('/', { replace: true });
      }
    })();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="flex flex-col items-center gap-5">
        <BrandLogo size={64} />
        <div className="flex items-center gap-2 text-slate-700">
          <Loader2 size={16} className="animate-spin" /> Signing you in...
        </div>
      </div>
    </div>
  );
}
